import { useQuery } from '@tanstack/react-query';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';

import { getProductTimeline } from '../services/meta-delivery.service';
import type { ProductTimelineQuery } from '../types/meta-delivery.types';

/** How the UI should read a failed timeline lookup. */
export type TimelineErrorKind = 'no_history' | 'unavailable' | 'invalid' | 'error';

/**
 * Classify a timeline error: 404 = the product has no delivery history on this
 * connection (an empty state, and the message names the idType used); 503 = the
 * history store can't answer (a retry affordance, NOT an empty state); 422 = a
 * bad idType / limit / offset; anything else is generic.
 */
export function classifyTimelineError(err: unknown): TimelineErrorKind {
  const status = err instanceof ApiError ? err.statusCode : null;
  if (status === 404) return 'no_history';
  if (status === 503) return 'unavailable';
  if (status === 422) return 'invalid';
  return 'error';
}

/**
 * One product's cross-job audit timeline on a connection. Only a 503 is retried
 * (twice) — a 404 is a legitimate answer, and right after a generation it can be
 * a legitimate *transient* one (per-product rows are materialised by a background
 * workflow), which the caller handles by offering "check again".
 */
export function useProductTimeline(
  connectionId: string,
  productId: string,
  query: ProductTimelineQuery = {},
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.deliveries.productTimeline(
      connectionId,
      productId,
      query as Record<string, unknown>,
    ),
    queryFn: () => getProductTimeline(connectionId, productId, query),
    enabled: enabled && !!connectionId && productId.trim() !== '',
    staleTime: 15_000,
    retry: (failureCount, err) => classifyTimelineError(err) === 'unavailable' && failureCount < 2,
  });
}
